import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Swal from 'sweetalert2';
import { leaveAPI, employeeAPI, workRequestAPI } from '../services/api';
import { Calendar, FileText, Settings, Users, Gift, Plus, Edit, Trash, Check, X } from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

const LeaveManagement = ({ currentTab = 'requests' }) => {
  const navigate = useNavigate();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const [newPolicy, setNewPolicy] = useState({
    name: '',
    description: '',
    sick_leave_annual: 10,
    casual_leave_annual: 10,
    annual_leave_annual: 20,
    allow_carry_forward: true,
    max_carry_forward_annual: 5,
    carry_forward_expiry_months: 3,
    requires_level2_approval_days: 5
  });

  const [newHoliday, setNewHoliday] = useState({
    name: '',
    date: '',
    is_optional: false,
    description: '',
    region: ''
  });
  const [onBehalfForm, setOnBehalfForm] = useState({
    request_type: 'leave',
    employee_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    attendance_date: '',
    from_date: '',
    to_date: '',
    work_location: '',
    work_plan: '',
    destination: '',
    purpose: '',
    travel_mode: '',
    contact_details: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    // allSettled, not all: these four are independent, and one of them failing
    // used to leave every list empty — most visibly the on-behalf employee
    // dropdown, which then looked broken rather than degraded.
    const [requestsRes, policiesRes, holidaysRes, employeesRes] = await Promise.allSettled([
      leaveAPI.list(),
      leaveAPI.getPolicies(),
      leaveAPI.getHolidays(new Date().getFullYear()),
      employeeAPI.list()
    ]);
    const apply = (result, setter, label) => {
      if (result.status === 'fulfilled') setter(result.value.data);
      else console.error(`Error fetching ${label}:`, result.reason);
    };
    apply(requestsRes, setLeaveRequests, 'leave requests');
    apply(policiesRes, setLeavePolicies, 'leave policies');
    apply(holidaysRes, setHolidays, 'holidays');
    apply(employeesRes, setEmployees, 'employees');
  };

  const handleApproveLeave = async (leaveId, level) => {
    try {
      if (level === 1) {
        await leaveAPI.approveLevel1(leaveId);
      } else {
        await leaveAPI.approveLevel2(leaveId);
      }
      Swal.fire({
        icon: 'success',
        title: 'Approved',
        text: 'Leave approved successfully',
        timer: 1500,
        showConfirmButton: false
      });
      fetchData();
    } catch (error) {
      Swal.fire('Error', 'Failed to approve leave', 'error');
    }
  };

  const handleRejectLeave = async (leaveId) => {
    const result = await Swal.fire({
      title: 'Reject Leave',
      input: 'text',
      inputLabel: 'Rejection reason',
      inputPlaceholder: 'Enter reason',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      confirmButtonColor: 'hsl(var(--destructive))'
    });
    if (!result.isConfirmed || !result.value) return;
    
    try {
      await leaveAPI.reject(leaveId, result.value);
      Swal.fire({
        icon: 'success',
        title: 'Rejected',
        text: 'Leave rejected successfully',
        timer: 1500,
        showConfirmButton: false
      });
      fetchData();
    } catch (error) {
      Swal.fire('Error', 'Failed to reject leave', 'error');
    }
  };

  const handleApplyOnBehalf = async () => {
    try {
      if (!onBehalfForm.employee_id) {
        Swal.fire('Validation', 'Please select an employee', 'warning');
        return;
      }
      if (onBehalfForm.request_type === 'leave') {
        await leaveAPI.applyOnBehalf({
          employee_id: onBehalfForm.employee_id,
          leave_type: onBehalfForm.leave_type,
          start_date: onBehalfForm.start_date,
          end_date: onBehalfForm.end_date,
          reason: onBehalfForm.reason,
        });
      } else if (onBehalfForm.request_type === 'wfh') {
        await workRequestAPI.applyWfhOnBehalf({
          employee_id: onBehalfForm.employee_id,
          from_date: onBehalfForm.from_date,
          to_date: onBehalfForm.to_date,
          reason: onBehalfForm.reason,
          work_location: onBehalfForm.work_location,
          work_plan: onBehalfForm.work_plan,
        });
      } else if (onBehalfForm.request_type === 'outstation') {
        await workRequestAPI.applyOutstationOnBehalf({
          employee_id: onBehalfForm.employee_id,
          from_date: onBehalfForm.from_date,
          to_date: onBehalfForm.to_date,
          reason: onBehalfForm.reason,
          destination: onBehalfForm.destination,
          purpose: onBehalfForm.purpose,
          travel_mode: onBehalfForm.travel_mode,
          contact_details: onBehalfForm.contact_details,
        });
      } else {
        await workRequestAPI.applyRegularizationOnBehalf({
          employee_id: onBehalfForm.employee_id,
          attendance_date: onBehalfForm.attendance_date,
          reason: onBehalfForm.reason,
        });
      }
      Swal.fire({
        icon: 'success',
        title: 'Submitted',
        text: 'Request applied on behalf successfully',
        timer: 1500,
        showConfirmButton: false
      });
      setOnBehalfForm({
        request_type: 'leave',
        employee_id: '',
        leave_type: 'casual',
        start_date: '',
        end_date: '',
        reason: '',
        attendance_date: '',
        from_date: '',
        to_date: '',
        work_location: '',
        work_plan: '',
        destination: '',
        purpose: '',
        travel_mode: '',
        contact_details: '',
      });
      fetchData();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error, 'Failed to apply on behalf'), 'error');
    }
  };

  const createPolicy = async () => {
    try {
      await leaveAPI.createPolicy(newPolicy);
      Swal.fire({
        icon: 'success',
        title: 'Created',
        text: 'Leave policy created',
        timer: 1500,
        showConfirmButton: false
      });
      setShowPolicyForm(false);
      setNewPolicy({
        name: '',
        description: '',
        sick_leave_annual: 10,
        casual_leave_annual: 10,
        annual_leave_annual: 20,
        allow_carry_forward: true,
        max_carry_forward_annual: 5,
        carry_forward_expiry_months: 3,
        requires_level2_approval_days: 5
      });
      fetchData();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error, 'Failed to create policy'), 'error');
    }
  };

  const updatePolicy = async () => {
    if (!editingPolicy) return;
    try {
      await leaveAPI.updatePolicy(editingPolicy.id, editingPolicy);
      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Policy updated',
        timer: 1500,
        showConfirmButton: false
      });
      setEditingPolicy(null);
      fetchData();
    } catch (error) {
      Swal.fire('Error', 'Failed to update policy', 'error');
    }
  };

  const deletePolicy = async (policyId) => {
    const result = await Swal.fire({
      title: 'Deactivate this policy?',
      text: 'You can reactivate by creating a new policy if needed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Deactivate',
      confirmButtonColor: 'hsl(var(--destructive))'
    });

    if (result.isConfirmed) {
      try {
        await leaveAPI.deletePolicy(policyId);
        Swal.fire({
          icon: 'success',
          title: 'Deactivated',
          text: 'Policy deactivated',
          timer: 1500,
          showConfirmButton: false
        });
        fetchData();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete policy', 'error');
      }
    }
  };

  const createHoliday = async () => {
    try {
      await leaveAPI.createHoliday(newHoliday);
      Swal.fire({
        icon: 'success',
        title: 'Created',
        text: 'Holiday created',
        timer: 1500,
        showConfirmButton: false
      });
      setShowHolidayForm(false);
      setNewHoliday({ name: '', date: '', is_optional: false, description: '', region: '' });
      fetchData();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error, 'Failed to create holiday'), 'error');
    }
  };

  const updateHoliday = async () => {
    if (!editingHoliday) return;
    try {
      await leaveAPI.updateHoliday(editingHoliday.id, {
        name: editingHoliday.name,
        date: editingHoliday.date,
        is_optional: editingHoliday.is_optional,
        description: editingHoliday.description,
        region: editingHoliday.region
      });
      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Holiday updated',
        timer: 1500,
        showConfirmButton: false
      });
      setEditingHoliday(null);
      fetchData();
    } catch (error) {
      Swal.fire('Error', 'Failed to update holiday', 'error');
    }
  };

  const deleteHoliday = async (holidayId) => {
    const result = await Swal.fire({
      title: 'Delete this holiday?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'hsl(var(--destructive))',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await leaveAPI.deleteHoliday(holidayId);
        Swal.fire('Deleted!', 'Holiday has been deleted.', 'success');
        fetchData();
      } catch (error) {
        Swal.fire('Error', 'Failed to delete holiday', 'error');
      }
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-[hsl(var(--chart-4))]/10 text-[hsl(var(--chart-4))]',
      pending_level2: 'bg-primary/10 text-primary',
      approved: 'bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))]',
      rejected: 'bg-destructive/10 text-destructive',
      cancelled: 'bg-muted text-muted-foreground'
    };
    return <Badge className={colors[status] || 'bg-muted text-muted-foreground'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-management">
        {/* Leave Requests Tab */}
        {currentTab === 'requests' && (
          <div className="space-y-6">
          <Card className="shadow-md border-0">
            <CardHeader className="bg-muted/30 border-b border-border pb-4">
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Apply On Behalf
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>Request Type</Label>
                  <Select value={onBehalfForm.request_type} onValueChange={(v) => setOnBehalfForm({ ...onBehalfForm, request_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="leave">Leave (incl. Maternity)</SelectItem>
                      <SelectItem value="wfh">Work From Home</SelectItem>
                      <SelectItem value="outstation">Outstation Duty</SelectItem>
                      <SelectItem value="regularization">Regularization</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Employee</Label>
                  <Select value={onBehalfForm.employee_id} onValueChange={(v) => setOnBehalfForm({ ...onBehalfForm, employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.employee_id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {onBehalfForm.request_type === 'leave' && (
                  <div>
                    <Label>Leave Type</Label>
                    <Select value={onBehalfForm.leave_type} onValueChange={(v) => setOnBehalfForm({ ...onBehalfForm, leave_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                        <SelectItem value="maternity">Maternity</SelectItem>
                        <SelectItem value="compensatory">Comp Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {onBehalfForm.request_type === 'leave' && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>Start Date</Label><Input type="date" value={onBehalfForm.start_date} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, start_date: e.target.value })} /></div>
                  <div><Label>End Date</Label><Input type="date" value={onBehalfForm.end_date} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, end_date: e.target.value })} /></div>
                </div>
              )}
              {onBehalfForm.request_type === 'regularization' && (
                <div><Label>Attendance Date</Label><Input type="date" value={onBehalfForm.attendance_date} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, attendance_date: e.target.value })} /></div>
              )}
              {(onBehalfForm.request_type === 'wfh' || onBehalfForm.request_type === 'outstation') && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>From Date</Label><Input type="date" value={onBehalfForm.from_date} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, from_date: e.target.value })} /></div>
                  <div><Label>To Date</Label><Input type="date" value={onBehalfForm.to_date} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, to_date: e.target.value })} /></div>
                </div>
              )}
              {onBehalfForm.request_type === 'wfh' && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>Work Location</Label><Input value={onBehalfForm.work_location} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, work_location: e.target.value })} /></div>
                  <div><Label>Work Plan</Label><Input value={onBehalfForm.work_plan} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, work_plan: e.target.value })} /></div>
                </div>
              )}
              {onBehalfForm.request_type === 'outstation' && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><Label>Destination</Label><Input value={onBehalfForm.destination} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, destination: e.target.value })} /></div>
                  <div><Label>Purpose</Label><Input value={onBehalfForm.purpose} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, purpose: e.target.value })} /></div>
                </div>
              )}

              <div>
                <Label>Reason</Label>
                <Input value={onBehalfForm.reason} onChange={(e) => setOnBehalfForm({ ...onBehalfForm, reason: e.target.value })} />
              </div>
              <Button onClick={handleApplyOnBehalf}>Submit On Behalf</Button>
            </CardContent>
          </Card>
          <Card className="shadow-md border-0">
            <CardHeader className="bg-muted/30 border-b border-border pb-4">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Pending Leave Requests
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {leaveRequests.filter(r => r.status === 'pending' || r.status === 'pending_level2').map((request, index) => (
                  <div 
                    key={request.id} 
                    className="p-4 border border-border rounded-xl hover:bg-muted/30 transition-all duration-200 hover:shadow-sm hover:border-primary/30 animate-slide-up bg-card" 
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`leave-request-${request.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-lg text-foreground">{request.employee_name}</span>
                          {getStatusBadge(request.status)}
                          <Badge variant="outline" className="bg-card text-foreground border-border">{request.leave_type}</Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {request.start_date} to {request.end_date}
                          </div>
                          <Badge variant="secondary" className="font-normal bg-secondary text-secondary-foreground">
                            {request.total_days} days
                          </Badge>
                        </div>
                        {request.reason && (
                          <div className="mt-3 text-sm bg-muted/30 p-2 rounded-md border border-border text-muted-foreground">
                            <span className="font-medium text-foreground mr-1">Reason:</span>
                            {request.reason}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button 
                          size="sm" 
                          className="bg-[hsl(var(--chart-2))] hover:bg-[hsl(var(--chart-2))]/90 text-white shadow-sm hover:shadow active:scale-95 transition-all"
                          onClick={() => handleApproveLeave(request.id, request.status === 'pending' ? 1 : 2)}
                          data-testid={`approve-leave-${request.id}`}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approve {request.status === 'pending_level2' ? 'L2' : ''}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="shadow-sm hover:shadow active:scale-95 transition-all"
                          onClick={() => handleRejectLeave(request.id)}
                          data-testid={`reject-leave-${request.id}`}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {leaveRequests.filter(r => r.status === 'pending' || r.status === 'pending_level2').length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No pending leave requests</p>
                )}
              </div>

              <div className="mt-8">
                <h3 className="font-semibold text-lg mb-4 text-foreground flex items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  All Leave Requests
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                  {leaveRequests.map((request, index) => (
                    <div 
                      key={request.id} 
                      className="p-3 border border-border rounded-lg text-sm hover:bg-muted/30 transition-colors flex justify-between items-center group animate-slide-up bg-card"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-medium text-xs">
                          {request.employee_name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-foreground">
                            {request.employee_name}
                            <span className="mx-2 text-muted-foreground">•</span>
                            <span className="text-muted-foreground">{request.leave_type}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {request.start_date} to {request.end_date} · {request.total_days} days
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(request.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Leave Policies Tab */}
        {currentTab === 'policies' && (
          <>
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Leave Policies</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => navigate('/admin/import/leave_policy')}>
                    Import Policies
                  </Button>
                  <Dialog open={showPolicyForm} onOpenChange={setShowPolicyForm}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-policy-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Policy
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create Leave Policy</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                      <div>
                        <Label>Policy Name</Label>
                        <Input
                          value={newPolicy.name}
                          onChange={(e) => setNewPolicy({...newPolicy, name: e.target.value})}
                          placeholder="e.g., Standard, Executive"
                          data-testid="policy-name-input"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={newPolicy.description}
                          onChange={(e) => setNewPolicy({...newPolicy, description: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label>Sick Leave</Label>
                          <Input
                            type="number"
                            value={newPolicy.sick_leave_annual}
                            onChange={(e) => setNewPolicy({...newPolicy, sick_leave_annual: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div>
                          <Label>Casual Leave</Label>
                          <Input
                            type="number"
                            value={newPolicy.casual_leave_annual}
                            onChange={(e) => setNewPolicy({...newPolicy, casual_leave_annual: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div>
                          <Label>Annual Leave</Label>
                          <Input
                            type="number"
                            value={newPolicy.annual_leave_annual}
                            onChange={(e) => setNewPolicy({...newPolicy, annual_leave_annual: parseFloat(e.target.value)})}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Max Carry Forward</Label>
                          <Input
                            type="number"
                            value={newPolicy.max_carry_forward_annual}
                            onChange={(e) => setNewPolicy({...newPolicy, max_carry_forward_annual: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div>
                          <Label>CF Expiry (months)</Label>
                          <Input
                            type="number"
                            value={newPolicy.carry_forward_expiry_months}
                            onChange={(e) => setNewPolicy({...newPolicy, carry_forward_expiry_months: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Level 2 Approval Required (days)</Label>
                        <Input
                          type="number"
                          value={newPolicy.requires_level2_approval_days}
                          onChange={(e) => setNewPolicy({...newPolicy, requires_level2_approval_days: parseInt(e.target.value)})}
                        />
                      </div>
                      <Button onClick={createPolicy} className="w-full" data-testid="save-policy-btn">
                        Create Policy
                      </Button>
                    </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leavePolicies.map((policy, index) => (
                  <div 
                    key={policy.id} 
                    className="p-4 border rounded-lg hover:shadow-md transition-all duration-200 hover:border-primary/50 animate-slide-up" 
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`policy-${policy.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{policy.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{policy.description}</p>
                        <div className="flex gap-4 mt-3 text-sm">
                          <span className="bg-destructive/10 text-destructive px-2 py-1 rounded-md text-xs font-medium border border-destructive/20">Sick: {policy.sick_leave_annual}d</span>
                          <span className="bg-primary/10 text-primary px-2 py-1 rounded-md text-xs font-medium border border-primary/20">Casual: {policy.casual_leave_annual}d</span>
                          <span className="bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] px-2 py-1 rounded-md text-xs font-medium border border-[hsl(var(--chart-2))]/20">Annual: {policy.annual_leave_annual}d</span>
                        </div>
                        <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Carry Forward: {policy.allow_carry_forward ? `Max ${policy.max_carry_forward_annual}d` : 'No'}</span>
                          <span>L2 Approval: >{policy.requires_level2_approval_days}d</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setEditingPolicy(policy)}
                          data-testid={`edit-policy-${policy.id}`}
                          className="hover:bg-primary/10 hover:text-primary dark:hover:text-primary-foreground transition-colors"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => deletePolicy(policy.id)}
                          className="hover:bg-destructive/10 hover:text-destructive transition-colors"
                        >
                          <Trash className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Edit Policy Dialog */}
          {editingPolicy && (
            <Dialog open={!!editingPolicy} onOpenChange={() => setEditingPolicy(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Policy: {editingPolicy.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={editingPolicy.description || ''}
                      onChange={(e) => setEditingPolicy({...editingPolicy, description: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Sick Leave</Label>
                      <Input
                        type="number"
                        value={editingPolicy.sick_leave_annual}
                        onChange={(e) => setEditingPolicy({...editingPolicy, sick_leave_annual: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Casual Leave</Label>
                      <Input
                        type="number"
                        value={editingPolicy.casual_leave_annual}
                        onChange={(e) => setEditingPolicy({...editingPolicy, casual_leave_annual: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Annual Leave</Label>
                      <Input
                        type="number"
                        value={editingPolicy.annual_leave_annual}
                        onChange={(e) => setEditingPolicy({...editingPolicy, annual_leave_annual: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Max Carry Forward</Label>
                      <Input
                        type="number"
                        value={editingPolicy.max_carry_forward_annual}
                        onChange={(e) => setEditingPolicy({...editingPolicy, max_carry_forward_annual: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Level 2 Days Threshold</Label>
                      <Input
                        type="number"
                        value={editingPolicy.requires_level2_approval_days}
                        onChange={(e) => setEditingPolicy({...editingPolicy, requires_level2_approval_days: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  <Button onClick={updatePolicy} className="w-full bg-primary hover:bg-primary/90 shadow-sm hover:shadow active:scale-95 transition-all text-primary-foreground">Update Policy</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          </>
        )}

        {/* Public Holidays Tab */}
        {currentTab === 'holidays' && (
          <>
          <Card className="shadow-md border-0">
            <CardHeader className="bg-muted/30 border-b border-border pb-4">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-primary" />
                  Public Holidays {new Date().getFullYear()}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => navigate('/admin/import/holiday')}>
                    Import Holidays
                  </Button>
                  <Dialog open={showHolidayForm} onOpenChange={setShowHolidayForm}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-holiday-btn" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow active:scale-95 transition-all">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Holiday
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Add Public Holiday</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Holiday Name</Label>
                        <Input
                          value={newHoliday.name}
                          onChange={(e) => setNewHoliday({...newHoliday, name: e.target.value})}
                          placeholder="e.g., Independence Day"
                          data-testid="holiday-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={newHoliday.date}
                            onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})}
                            data-testid="holiday-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Region</Label>
                          <Input
                            value={newHoliday.region}
                            onChange={(e) => setNewHoliday({...newHoliday, region: e.target.value})}
                            placeholder="e.g., Karnataka"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-md border border-border">
                        <Checkbox
                          checked={newHoliday.is_optional}
                          onCheckedChange={(checked) => setNewHoliday({...newHoliday, is_optional: checked})}
                          id="is-optional"
                        />
                        <Label htmlFor="is-optional" className="cursor-pointer">Optional Holiday</Label>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          value={newHoliday.description}
                          onChange={(e) => setNewHoliday({...newHoliday, description: e.target.value})}
                          placeholder="Brief description"
                        />
                      </div>
                      <Button onClick={createHoliday} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-holiday-btn">
                        Add Holiday
                      </Button>
                    </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {holidays.map((holiday, index) => (
                  <div 
                    key={holiday.id} 
                    className="p-4 border border-border rounded-xl flex justify-between items-center bg-card hover:border-primary/30 hover:shadow-md transition-all duration-300 group animate-slide-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`holiday-${holiday.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex flex-col items-center justify-center text-primary-foreground shadow-md transform group-hover:scale-105 transition-transform duration-300">
                        <span className="text-[10px] uppercase font-medium tracking-wider opacity-90">
                          {new Date(holiday.date).toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                        <span className="text-2xl font-bold leading-none">
                          {new Date(holiday.date).getDate()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-foreground">{holiday.name}</span>
                          {holiday.region && (
                            <Badge variant="secondary" className="text-[10px] h-5 bg-secondary text-secondary-foreground">{holiday.region}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {holiday.is_optional && (
                            <Badge variant="outline" className="text-[10px] h-5 border-primary/30 text-primary bg-primary/10">Optional</Badge>
                          )}
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">{holiday.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-8 w-8 p-0 hover:bg-muted rounded-full"
                        onClick={() => setEditingHoliday(holiday)}
                        data-testid={`edit-holiday-${holiday.id}`}
                      >
                        <Edit className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-8 w-8 p-0 hover:bg-destructive/10 rounded-full hover:text-destructive"
                        onClick={() => deleteHoliday(holiday.id)}
                      >
                        <Trash className="h-4 w-4 text-muted-foreground group-hover:text-destructive transition-colors" />
                      </Button>
                    </div>
                  </div>
                ))}
                {holidays.length === 0 && (
                  <div className="col-span-full p-8 border border-dashed border-border rounded-xl text-center text-muted-foreground">
                    No holidays available for this year.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit Holiday Dialog */}
          {editingHoliday && (
            <Dialog open={!!editingHoliday} onOpenChange={() => setEditingHoliday(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Holiday</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Holiday Name</Label>
                    <Input
                      value={editingHoliday.name}
                      onChange={(e) => setEditingHoliday({...editingHoliday, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={editingHoliday.date}
                      onChange={(e) => setEditingHoliday({...editingHoliday, date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Region</Label>
                    <Input
                      value={editingHoliday.region || ''}
                      onChange={(e) => setEditingHoliday({...editingHoliday, region: e.target.value})}
                      placeholder="e.g., Karnataka, National"
                    />
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-md border border-border">
                    <Checkbox
                      checked={editingHoliday.is_optional}
                      onCheckedChange={(checked) => setEditingHoliday({...editingHoliday, is_optional: checked})}
                      id="edit-is-optional"
                    />
                    <Label htmlFor="edit-is-optional" className="cursor-pointer">Optional Holiday</Label>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={editingHoliday.description || ''}
                      onChange={(e) => setEditingHoliday({...editingHoliday, description: e.target.value})}
                    />
                  </div>
                  <Button onClick={updateHoliday} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow active:scale-95 transition-all">Update Holiday</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          </>
        )}
    </div>
  );
};

export default LeaveManagement;
